'use client';

import { useEffect, useState } from 'react';
import { Close } from '@mui/icons-material';
import { IconButton, type SxProps, alpha, useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import { isFalsy } from 'utility-types';
import type { BaseModalProps, ModalButtons } from '@/interfaces/component/modal.interface';
import { getDeviceType } from '@/utils/browser.util';
import EmptyCompnent from '@/components/EmptyComponent';
import FlexBox from '@/components/FlexBox';
import CustomMuiButton from '@/components/button/CustomMuiButton';
import useCheckIsMobileSize from '@/hooks/useCheckIsMobileSize';

const backGroundStyle: SxProps = {
  width: '100vw',
  height: '100vh',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  bgColor: alpha('#000000', 0.7)
};
export const modalStyle: SxProps = {
  width: '100%',
  height: '100%',
  maxHeight: '800px',
  maxWidth: '530px',
  bgcolor: 'white',
  boxShadow: '0px 20px 24px -4px rgba(16, 24, 40, 0.08), 0px 8px 8px -4px rgba(16, 24, 40, 0.03)',
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  padding: '0',
  overflow: 'hidden'
};

export const modalHeaderStyle: SxProps = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '24px'
};

const ModalHeader = ({ modalTitle, closeModalWithNoEffect }: { modalTitle?: string; closeModalWithNoEffect: () => void }) => {
  return (
    <Box component="div" sx={modalTitle ? modalHeaderStyle : { ...modalHeaderStyle, padding: '12px' }}>
      <Typography id="transition-modal-title" variant="h3" component="h3">
        {modalTitle ?? ''}
      </Typography>
      <IconButton onClick={closeModalWithNoEffect} sx={{ padding: 0 }} aria-label="모달 닫기">
        <Close />
      </IconButton>
    </Box>
  );
};

const ModalContents = ({ children, isFullHeight }: { children: React.ReactNode; isFullHeight: boolean }) => {
  return children ? (
    <FlexBox
      style={{
        paddingX: '20px',
        flex: 1,
        height: isFullHeight ? 'calc(100vh - 86px - 94px)' : 'auto',
        maxHeight: isFullHeight ? 'auto' : 'calc(100vh - 86px - 94px - 32px)',
        flexDirection: 'column',
        justifyContent: 'start',
        overflowY: 'auto'
      }}
    >
      {children}
    </FlexBox>
  ) : (
    <EmptyCompnent />
  );
};

const ModalBottom = ({ buttons }: { buttons: ModalButtons }) => {
  const { primary, secondary } = buttons;

  const primaryStyle = {
    ...(primary?.style ?? {}),
    [isFalsy(secondary) ? 'width' : 'flex']: 1
  };
  const secondaryStyle = { ...(secondary?.style ?? {}), flex: 1 };

  return (
    <FlexBox
      style={{
        justifyContent: 'space-between',
        padding: '20px',
        gap: '20px',
        backgroundColor: 'white'
      }}
    >
      {!isFalsy(secondary) ? <CustomMuiButton type="SECONDARY" sx={secondaryStyle} label={secondary.title} onClick={secondary.onClick} disabled={secondary.disabled} /> : ''}
      <CustomMuiButton type="PRIMARY" sx={primaryStyle} label={primary.title} onClick={primary.onClick} disabled={primary.disabled} />
    </FlexBox>
  );
};

const BaseModal = (props: BaseModalProps) => {
  const { modalTitle, showModal, onCloseModal, handleShowModal, children, buttons, closeButtonTitle = '닫기', isFullHeight = false, style = {} } = props;

  const isMobile = useCheckIsMobileSize();
  const isNotTablet = useMediaQuery('(max-width:430px)');

  const [isSafariMobile, setIsSafariMobile] = useState(false);

  useEffect(() => {
    const { device, browser } = getDeviceType();
    setIsSafariMobile(device === 'IOS' && browser === 'SAFARI');
  }, []);

  const closeModal = () => {
    if (onCloseModal) {
      onCloseModal();
    }
    handleShowModal();
  };

  let borderRadius: string | number = '20px';

  const _modalStyle = {
    ...modalStyle
  };
  const _backGroundStyle = {
    ...backGroundStyle
  };

  if (isFullHeight && isMobile) {
    if (isNotTablet) {
      borderRadius = 0;
      _modalStyle.height = '100vh';
      _modalStyle.maxHeight = '100vh';
    }
    _backGroundStyle.bgColor = 'white';
  } else if (!isFullHeight) {
    _modalStyle.height = 'auto';
  }

  if (isSafariMobile && isFullHeight) {
    _modalStyle.padding = '0 0 calc(100vh - 100dvh) 0';
  }

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={showModal}
      onClose={closeModal}
      closeAfterTransition
      sx={isMobile && !isFullHeight ? { ..._backGroundStyle, paddingX: '12px' } : _backGroundStyle}
    >
      <Fade in={showModal}>
        <Box
          sx={{
            ..._modalStyle,
            borderRadius: borderRadius,
            ...(isMobile && isFullHeight ? {} : style)
          }}
        >
          <ModalHeader modalTitle={modalTitle ?? ''} closeModalWithNoEffect={handleShowModal} />

          <ModalContents isFullHeight={isMobile && isFullHeight}>{children}</ModalContents>

          <ModalBottom
            buttons={
              buttons ?? {
                primary: {
                  title: closeButtonTitle,
                  onClick: () => closeModal()
                }
              }
            }
          />
        </Box>
      </Fade>
    </Modal>
  );
};

export default BaseModal;
